import type { Row } from '@tanstack/react-table';

import type { Importer } from '@feathr/blackbox';

/**
 * Whether a row expands to the run-details panel. Every draft, pending and failed import does, and
 * the panel says what state it is in. A complete import does once the worker stamped
 * `date_started` (Feathr/anhinga#8511): one the old importer ran has no stamp, and keeps today's
 * row (Feathr/anhinga#8646 gates the screens on the record, not only on the account's flags).
 */
export function hasRunDetails(importer: Importer): boolean {
  if (importer.get('state') === 'complete') {
    return Boolean(importer.get('date_started'));
  }
  return true;
}

/**
 * Whether the run has started: the worker stamped `date_started` and the import is past draft.
 * Only a started run has an Initiated-by line.
 */
export function hasRunStarted(importer: Importer): boolean {
  return importer.get('state') !== 'draft' && Boolean(importer.get('date_started'));
}

/**
 * How many rows a fully imported run wrote: a complete import that skipped none. The count is the
 * people it created plus the people it updated. `undefined` for any other import, including a
 * complete one that wrote nothing.
 */
export function getFullyImportedRowCount(importer: Importer): number | undefined {
  if (importer.get('state') !== 'complete' || importer.get('skipped', 0) > 0) {
    return undefined;
  }
  const count = importer.get('inserted', 0) + importer.get('duplicates', 0);
  return count > 0 ? count : undefined;
}

/** `TanStackTable`'s `getRowCanExpand`, at module scope so its identity is stable. */
export function canExpandImportRow(row: Row<Importer>): boolean {
  return hasRunDetails(row.original);
}
